import { StoreDoc } from '@gooduncles/gu-app-schema';
import { ICellEditorParams } from 'ag-grid-community';
import { Button, Form, Image, Input, InputNumber, Modal, Popconfirm, Space, Table, message } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { FC, useEffect, useState } from 'react';
import { updateStore } from 'src/utils/firebase/firebase-short-cut';

interface MenuItemType {
  key: string;
  name: string;
  price: number | null;
  description: string | null;
  images: string[];
}

const AgCellUpdateStoreMenuByNaverPlace: FC<ICellEditorParams<StoreDoc>> = ({ stopEditing, data, value }) => {
  const storeId = data._id;
  const [form] = Form.useForm();
  const [menuItems, setMenuItems] = useState<MenuItemType[]>([]);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(true);
  const [editingKey, setEditingKey] = useState<string>('');

  useEffect(() => {
    if (value && Array.isArray(value)) {
      const items = value.map((item, index) => ({
        key: index.toString(),
        name: item.name,
        price: item.price,
        description: item.description,
        images: item.images ?? [],
      }));
      setMenuItems(items);
    }
  }, [value]);

  const isEditing = (record: MenuItemType) => record.key === editingKey;

  const edit = (record: MenuItemType) => {
    form.setFieldsValue({
      name: record.name,
      price: record.price,
      description: record.description,
      images: record.images,
    });
    setEditingKey(record.key);
  };

  const cancel = () => {
    setEditingKey('');
  };

  const save = async (key: string) => {
    try {
      const row = await form.validateFields();
      const newData = [...menuItems];
      const index = newData.findIndex((item) => key === item.key);

      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, { ...item, ...row });
        setMenuItems(newData);
        setEditingKey('');
      }
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  const handleDelete = (key: string) => {
    const newData = menuItems.filter((item) => item.key !== key);
    setMenuItems(newData);
  };

  const handleAdd = () => {
    const newKey = (menuItems.length + 1).toString();
    const newItem: MenuItemType = {
      key: newKey,
      name: '',
      price: null,
      description: null,
      images: [],
    };
    setMenuItems([...menuItems, newItem]);
    edit(newItem);
  };

  const handleOk = async () => {
    const formattedMenuItems = menuItems.map((item) => ({
      name: item.name,
      price: item.price,
      description: item.description,
      images: item.images,
    }));

    if (!storeId) {
      message.error('매장 ID가 없습니다.');
      return;
    }

    try {
      await updateStore(storeId, {
        storeMenu: formattedMenuItems,
      });
      message.success('메뉴가 업데이트되었습니다.');
    } catch (error) {
      console.error(error);
      message.error('메뉴 업데이트에 실패했습니다.');
    }
    setIsModalVisible(false);
    stopEditing();
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    stopEditing();
  };

  const columns = [
    {
      title: '메뉴명',
      dataIndex: 'name',
      key: 'name',
      editable: true,
      width: '25%',
    },
    {
      title: '가격',
      dataIndex: 'price',
      key: 'price',
      editable: true,
      width: '15%',
      render: (price: number | null) => (price ? `${price.toLocaleString()}원` : '-'),
    },
    {
      title: '설명',
      dataIndex: 'description',
      key: 'description',
      editable: true,
      width: '40%',
    },
    {
      title: '이미지',
      dataIndex: 'images',
      key: 'images',
      editable: true,
      width: '20%',
      render: (images: string[]) =>
        images?.length ? (
          <Image.PreviewGroup>
            <Space size={4} wrap>
              {images.map((url, i) => (
                <Image key={i} src={url} width={36} height={36} style={{ objectFit: 'cover', borderRadius: 4 }} />
              ))}
            </Space>
          </Image.PreviewGroup>
        ) : (
          '-'
        ),
    },
    {
      title: '작업',
      key: 'action',
      render: (_: any, record: MenuItemType) => {
        const editable = isEditing(record);
        return editable ? (
          <Space>
            <Button type='link' onClick={() => save(record.key)}>
              저장
            </Button>
            <Button type='link' onClick={cancel}>
              취소
            </Button>
          </Space>
        ) : (
          <Space>
            <Button type='link' disabled={editingKey !== ''} onClick={() => edit(record)}>
              수정
            </Button>
            <Popconfirm title='이 메뉴를 삭제하시겠습니까?' onConfirm={() => handleDelete(record.key)}>
              <Button type='link' danger>
                삭제
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: MenuItemType) => ({
        record,
        inputType: col.dataIndex === 'price' ? 'number' : col.dataIndex === 'images' ? 'images' : 'text',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  const EditableCell = ({ editing, dataIndex, title, inputType, children, ...restProps }: any) => {
    if (!editing) {
      return <td {...restProps}>{children}</td>;
    }

    if (inputType === 'images') {
      return (
        <td {...restProps}>
          <Form.List name={dataIndex}>
            {(fields, { add, remove }) => (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {fields.map((field) => (
                  <Space key={field.key} align='start' size={4}>
                    <Form.Item {...field} style={{ margin: 0, flex: 1 }}>
                      <Input placeholder='이미지 URL' size='small' />
                    </Form.Item>
                    <MinusCircleOutlined
                      onClick={() => remove(field.name)}
                      style={{ marginTop: 8, color: '#ff4d4f' }}
                    />
                  </Space>
                ))}
                <Button type='dashed' onClick={() => add('')} size='small' icon={<PlusOutlined />}>
                  URL 추가
                </Button>
              </div>
            )}
          </Form.List>
        </td>
      );
    }

    const inputNode =
      inputType === 'number' ? (
        <InputNumber min={0} formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} />
      ) : (
        <Input />
      );

    return (
      <td {...restProps}>
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={dataIndex === 'name' ? [{ required: true, message: `${title}은(는) 필수 입력 항목입니다!` }] : []}
        >
          {inputNode}
        </Form.Item>
      </td>
    );
  };

  return (
    <Modal
      title='메뉴 수정'
      open={isModalVisible}
      onOk={handleOk}
      onCancel={handleCancel}
      width={800}
      centered
      styles={{
        body: {
          maxHeight: '80vh',
          overflow: 'auto',
          paddingBottom: 16,
        },
      }}
      footer={[
        <Button key='back' onClick={handleCancel}>
          취소
        </Button>,
        <Button key='submit' type='primary' onClick={handleOk}>
          저장
        </Button>,
      ]}
    >
      <Button type='primary' onClick={handleAdd} style={{ marginBottom: 16 }}>
        메뉴 추가
      </Button>
      <Form form={form} component={false}>
        <Table
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          bordered
          dataSource={menuItems}
          columns={mergedColumns}
          rowClassName='editable-row'
          pagination={false}
        />
      </Form>
    </Modal>
  );
};

export default AgCellUpdateStoreMenuByNaverPlace;
